import {
	InvoiceParamsFromPosDTO,
	Res,
	TicketLocationDTO,
	TicketDTO,
	BuyerCustomerDTO,
	SaleDTO,
	TipPaymentDTO,
	TicketLineDTO
} from 'core/api';

import { Result, CustomError, ServerError, handleServerErrors, Events } from 'core/infrastructure';
import { InvoiceFacade, PrintFacade } from 'pos/facades';

export async function reopen(invoiceId: number, location: TicketLocationDTO): Promise<Result<TicketDTO, CustomError>> {
	return execute(async () => await InvoiceFacade.reopen(invoiceId, location));
}

export async function reopenInTicket(invoiceId: number, location: TicketLocationDTO, targetTicketId: number): Promise<Result<TicketDTO, CustomError>> {
	return execute(async () => await InvoiceFacade.reopen(invoiceId, location, targetTicketId));
}

export async function print(invoiceId: number, params: PrintFacade.PrintParams): Promise<void> {
	await execute(async () => await PrintFacade.printInvoiceOnPrinter(invoiceId, params));
}

export async function printGift(invoiceId: number, lines: TicketLineDTO[]): Promise<void> {
	await execute(async () => await PrintFacade.printInvoiceGiftLines(invoiceId, lines.map(l => l.Id)));
}

export async function convertToStandard(invoiceId: number, customer: BuyerCustomerDTO): Promise<void> {
	await execute(async () => await InvoiceFacade.convertToStandard(invoiceId, customer));
}

export async function printCoupons(invoiceId: number): Promise<void> {
	await execute(async () => await PrintFacade.printInvoiceCoupons(invoiceId));
}

export async function addTip(invoiceId: number, tip: TipPaymentDTO): Promise<Result<TipPaymentDTO[]>> {
	return await execute(async () => {
		const updatedTips = await InvoiceFacade.addTip(invoiceId, tip);
		await Events.raise('pos:invoice:changed');
		return updatedTips;
	});
}

export async function setCustomer(invoiceId: number, customer: BuyerCustomerDTO): Promise<void> {
	await execute(async () => {
		await InvoiceFacade.setCustomer(invoiceId, customer);
		await Events.raise('pos:invoice:changed');
	});
}

export async function updateCustomerData(invoiceId: number): Promise<void> {
	await execute(async () => {
		await InvoiceFacade.updateCustomerData(invoiceId);
		await Events.raise('pos:invoice:changed');
	});
}

export async function refund(invoiceId: number, params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	return execute(async () => {
		const result = await InvoiceFacade.refund(invoiceId, params);
		await Events.raise('pos:invoice:changed');
		return result;
	});
}

export async function changePayment(invoiceId: number, params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	return execute(async () => {
		const result = await InvoiceFacade.changePayment(invoiceId, params);
		await Events.raise('pos:invoice:changed');

		return result;
	});
}

export async function setTableCompanions(invoiceId: number, tableCompanions: Nullable<number>): Promise<void> {
	await execute(async () => {
		await InvoiceFacade.setTableCompanions(invoiceId, tableCompanions);
		await Events.raise('pos:invoice:changed');
	});
}

export async function createTicketFrom(invoiceId: number): Promise<Result<TicketDTO>> {
	return execute(async () => {
		const result = await InvoiceFacade.createTicketFromInvoice(invoiceId);
		return result;
	});
}

export async function deleteTicketFromInvoice(ticketId: number): Promise<Result> {
	return execute(async () => {
		const result = await InvoiceFacade.deleteTicketFromInvoice(ticketId);
		return result;
	});
}

async function execute<T>(fn: () => Promise<T>): Promise<Result<T, CustomError>> {

	let handledError: CustomError = new ServerError('ERROR NO MANEJADO');

	const result = await handleServerErrors(fn, {

		onPrintError: async error => {
			handledError = error;
			await Events.raise('pos:invoice:operation-failed', { msg: Res.Msg.MSG_TICKET_COULD_NOT_BE_PRINTED(error.message), error });
		},

		onInvalidPaymentError: async error => {
			handledError = error;
			await Events.raise('pos:invoice:operation-failed', { msg: error.message, error });
		},

		onDomainError: async error => {
			handledError = error;
			await Events.raise('pos:invoice:operation-failed', { msg: error.message, error });
		},


		onFrozenPaymentsError: async error => {
			handledError = error;
			await Events.raise('pos:invoice:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_FROZEN_PAYMENTS_SO_CHECK_THE_NEW_COMPOSITION(), error });
		},

		onInvalidCustomerCifError: async error => {
			handledError = error;
			await Events.raise('pos:invoice:operation-failed', { msg: Res.Msg.MSG_INVALID_CUSTOMER_CIF_TO_CREATE_STANDARD_INVOICE(), error });
		}
	});

	return result.ok ? Result.ok(result.value) : Result.error(handledError);
}
